import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import SHA256 from "crypto-js/sha256";
import { createColumnHelper } from "@tanstack/react-table";
import { AuthContext, CONTEXT_KEY } from "../Auth/AuthContext";
import {
  TextButton,
  IconButton,
  Table,
  TableCell,
  tableCellTypesEnum,
  tableFilterTypes,
  ModalContext,
  Label,
  LabelVariant,
} from "@SeemplicityDev/shared";
import AccountActionsPopover from "./AccountActionsPopover";
import OrganizationActions from "./OrganizationActions";
import { useFlags } from "launchdarkly-react-client-sdk";
import UpdateAccountType from "./UpdateAccountType/UpdateAccountType";
import { isImpersonationAllowed } from "./impersonationGate";
import type { AccountType } from "../utils";

// TODO unify: produced by CustomerProvisioning.js (parallel sibling); shape mirrors
// the flattened account rows built there from CncOrganization/CncAccount.
interface FlatAccount {
  organizationSlug: string;
  organizationName: string;
  accountUuid: string;
  accountName: string;
  ssoEnforced: boolean;
  domains: string[];
  primaryDomain: string;
  accountEnabled: boolean;
  organizationEnabled: boolean;
  databaseIdentifier: string;
  databaseSchema: string;
  accountType: AccountType;
  canDeleteOrganization: boolean;
}

interface OrganizationCellProps {
  row: FlatAccount;
}

const OrganizationCell: React.FC<OrganizationCellProps> = ({ row }) => {
  const [copiedSlugHash, setCopiedSlugHash] = useState<string | null>(null);

  const copySlugHash = useCallback(
    (slug: string) => {
      const hash = SHA256(slug).toString();
      setCopiedSlugHash(slug);
      navigator.clipboard.writeText(hash);
    },
    [setCopiedSlugHash]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (copiedSlugHash !== null) {
        setCopiedSlugHash(null);
      }
    }, 1800);
    return () => clearTimeout(timer);
  }, [copiedSlugHash]);

  return (
    <div>
      <div className="flex org">
        {`${row.organizationName} / ${row?.organizationSlug}`}
        <IconButton
          wrapperClassName={"copy"}
          type="button"
          onClick={() => copySlugHash(row?.organizationSlug)}
          size={"xs"}
          buttonStyle={
            copiedSlugHash === row?.organizationSlug ? "primary" : "secondary"
          }
          isIcon={
            copiedSlugHash === row?.organizationSlug
              ? "seem-icon-checkmark"
              : "seem-icon-copy"
          }
          withTooltip={{
            content: "Copy slug hash",
          }}
        />
      </div>
      <div className="text-xs">{row?.domains.join(", ")}</div>
    </div>
  );
};

interface AccountsTableProps {
  loading: boolean;
  accounts: FlatAccount[];
  cncReachable?: boolean;
}

const AccountsTable: React.FC<AccountsTableProps> = ({
  loading,
  accounts,
  cncReachable = true,
}) => {
  const history = useHistory();
  const client = useApolloClient();
  const { openModal, closeModal } = useContext(ModalContext);
  const { allowUsImpersonation } = useFlags();
  const { authState } = useContext(AuthContext);
  const [copiedAccountUuid, setCopiedAccountUuid] = useState<string | null>(
    null
  );

  const copyUuid = useCallback(
    (uuid: string) => {
      setCopiedAccountUuid(uuid);
      navigator.clipboard.writeText(uuid);
    },
    [setCopiedAccountUuid]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (copiedAccountUuid !== null) {
        setCopiedAccountUuid(null);
      }
    }, 1800);
    return () => clearTimeout(timer);
  }, [copiedAccountUuid]);

  const canImpersonate = (row: FlatAccount): boolean =>
    isImpersonationAllowed({
      organizationName: row.organizationName,
      allowUsImpersonation,
      username: authState.username,
    });

  const onImpersonateCustomer = (
    accountUuid: string,
    customerName: string,
    customerMainDomain: string
  ) => {
    window.localStorage.setItem(
      CONTEXT_KEY,
      JSON.stringify({
        id: accountUuid,
        customerName: customerName,
        mainDomain: customerMainDomain,
      })
    );

    try {
      client.stop();
      client.clearStore().finally(() => {
        history.push("/dashboard");
        window.location.reload();
      });
    } catch {
      history.push("/dashboard");
      window.location.reload();
    }
  };

  const columnHelper = createColumnHelper<FlatAccount>();

  const columns = [
    columnHelper.accessor("organizationName", {
      header: "Organization & Slug",
      cell: (info) =>
        info.row.getIsGrouped() &&
        info.row.groupingColumnId !== "organizationName" ? null : (
          <TableCell
            className="organization-name"
            type={tableCellTypesEnum.COMPONENT}
          >
            <OrganizationCell row={info.row?.original} />
            {!info.row.getIsGrouped() && (
              <OrganizationActions
                organizationSlug={info.row?.original?.organizationSlug}
                organizationName={info.row?.original?.organizationName}
                domains={info.row?.original?.domains}
                enabled={info.row?.original?.organizationEnabled}
                compact
                canDelete={info.row?.original?.canDeleteOrganization}
              />
            )}
          </TableCell>
        ),
      filterFn: tableFilterTypes.TEXT.filterFn,
      meta: {
        filterType: tableFilterTypes.TEXT.filterType,
        autoFocus: true,
        groupingLabel: "Organization",
      },
    }),
    columnHelper.accessor("accountName", {
      id: "accountName",
      header: "Account Name",
      filterFn: tableFilterTypes.TEXT.filterFn,
      meta: {
        filterType: tableFilterTypes.TEXT.filterType,
      },
      size: 100,
    }),
    columnHelper.accessor("accountUuid", {
      header: "Account UUID",
      cell: (info) => {
        if (info.row.getIsGrouped()) {
          return null;
        }
        const accountUuid = info.row?.original?.accountUuid;
        const isCopied = accountUuid === copiedAccountUuid;
        return (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <div className="account-uuid-wrap">
              <div className="account-uuid">{accountUuid}</div>
              <IconButton
                wrapperClassName={"copy"}
                type="button"
                onClick={() => copyUuid(accountUuid)}
                size={"xs"}
                buttonStyle={!isCopied && "secondary"}
                isIcon={isCopied ? "seem-icon-checkmark" : "seem-icon-copy"}
              />
            </div>
          </TableCell>
        );
      },
      filterFn: tableFilterTypes.TEXT.filterFn,
      meta: {
        filterType: tableFilterTypes.TEXT.filterType,
      },
    }),
    columnHelper.accessor(
      (row) => `${row.databaseIdentifier} / ${row.databaseSchema}`,
      {
        id: "database",
        header: "Database Identifier & Schema",
        filterFn: tableFilterTypes.TEXT.filterFn,
        meta: {
          filterType: tableFilterTypes.TEXT.filterType,
        },
      }
    ),
    columnHelper.accessor((row) => row.accountType, {
      cell: (info) =>
        info.row.getIsGrouped() ? (
          info.row.groupingColumnId === "accountType" ? (
            <TableCell type={tableCellTypesEnum.TEXT}>
              {info.row?.original?.accountType}
            </TableCell>
          ) : null
        ) : (
          <TableCell
            type={tableCellTypesEnum.COMPONENT}
            className="account-type-cell"
          >
            {info.row?.original?.accountType}
            {info.row?.original?.accountEnabled && (
              <IconButton
                isIcon="seem-icon-edit"
                size={"xs"}
                buttonStyle={"secondary"}
                withTooltip={{
                  content: "Edit Account Type",
                }}
                onClick={() =>
                  openModal({
                    title: `${info.row?.original?.accountName} - Edit Account Type`,
                    size: "s",
                    overlay: true,
                    component: (
                      <UpdateAccountType
                        accountName={info.row?.original?.accountName}
                        accountUuid={info.row?.original?.accountUuid}
                        accountType={info.row?.original?.accountType}
                        closeModal={closeModal}
                      />
                    ),
                  })
                }
              />
            )}
          </TableCell>
        ),
      id: "accountType",
      header: "Account Type",
      filterFn: tableFilterTypes.SELECT.filterFn,
      meta: {
        filterType: tableFilterTypes.SELECT.filterType,
        groupingLabel: "Account Type",
      },
      size: 120,
    }),
    columnHelper.accessor(
      (row) =>
        row.accountEnabled ? "Active Organization" : "Inactive Organization",
      {
        cell: (info) =>
          info.row.getIsGrouped() ? (
            <TableCell
              className={"form-button-group actions-cell"}
              type={tableCellTypesEnum.COMPONENT}
            >
              <TableCell
                className={"form-button-group actions-cell"}
                type={tableCellTypesEnum.COMPONENT}
              >
                <OrganizationActions
                  organizationSlug={info.row?.original?.organizationSlug}
                  organizationName={info.row?.original?.organizationName}
                  domains={info.row?.original?.domains}
                  enabled={info.row?.original?.organizationEnabled}
                  compact={false}
                  canDelete={info.row?.original?.canDeleteOrganization}
                />
              </TableCell>
            </TableCell>
          ) : (
            <TableCell
              className={"form-button-group actions-cell"}
              type={tableCellTypesEnum.COMPONENT}
            >
              <Label
                className="account-status-label"
                text={
                  info.row?.original?.accountEnabled ? "Active" : "Inactive"
                }
                variant={LabelVariant.Filled}
                disabled={!info.row?.original?.accountEnabled}
              />
              {info.row?.original?.accountEnabled && cncReachable && (
                <>
                  <TextButton
                    data-testid={`${info.row?.original?.accountUuid}-impersonate`}
                    label={"Impersonate"}
                    buttonStyle={"primary"}
                    withTooltip={{
                      content: !canImpersonate(info.row?.original)
                        ? "You are not allowed to impersonate this account"
                        : "",
                    }}
                    disabled={!canImpersonate(info.row?.original)}
                    onClick={() => {
                      onImpersonateCustomer(
                        info.row?.original?.accountUuid,
                        info.row?.original?.accountName,
                        info.row?.original?.primaryDomain
                      );
                    }}
                  />
                  <div className={"vertical-separator"} />
                </>
              )}
              <AccountActionsPopover
                accountName={info.row?.original?.accountName}
                accountUuid={info.row?.original?.accountUuid}
                organizationName={info.row?.original?.organizationName}
                domains={info.row?.original?.domains}
                openModal={openModal}
                accountEnabled={info.row?.original?.accountEnabled}
              />
            </TableCell>
          ),
        id: "actions",
        header: "Status",
        enableSorting: false,
        maxSize: 80,
        size: 80,
        filterFn: tableFilterTypes.SELECT.filterFn,
        meta: {
          filterType: tableFilterTypes.SELECT.filterType,
        },
      }
    ),
  ];

  return (
    <Table
      size={"large"}
      loading={loading}
      columns={columns}
      data={accounts}
      data-testid={"customers-list"}
      initialSortBy={[{ id: "organizationName" }, { id: "accountName" }]}
      initialFilters={[
        { id: "actions", value: "" },
        { id: "accountType", value: "" },
      ]}
    />
  );
};

export default AccountsTable;
